<template>
  <div class="container application">
    <div class="term-desc" />
    <h2>SMS 인증</h2>
    <div class="content">
      <small>신용 정보 조회 전 본인 확인을 위해 SMS 인증이 필요합니다.</small><br>
      <small>인증 완료 시 동의 및 신용 조회가 진행됩니다.</small>
    </div>

    <div class="input-item">
      <div class="row-member-sms">
        <div class="col-left">
          <input
            id="phoneNum"
            v-model="phoneNum"
            class="form-field form-field_short"
            type="number"
            name="phoneNum"
            data-vv-as="휴대폰번호"
            disabled="true"
          >
        </div>
        <div class="col-right">
          <button
            type="submit"
            class="btn btn_sm btn_full padding"
            :disabled="application.sendDim"
            @click="certifyPhoneNum"
          >
            인증번호 발송
          </button>
        </div>
      </div>
    </div>

    <div class="input-item mt-15">
      <input
        id="confirmNum"
        v-model="application.confirmNum"
        class="form-field form-field_short"
        placeholder="인증번호 입력"
        name="confirmNum"
        data-vv-as="휴대폰번호 인증"
        :disabled="application.verifyDim"
      >
      <vs-row>
        <vs-col class="height-20" vs-lg="10" vs-sm="10" vs-xs="10">
          <span v-if="application.showCountMessage" class="red050 small-text">유효기간이 만료되었습니다. 인증번호를 재발송해주세요.</span>
        </vs-col>
        <vs-col class="text-right height-20" vs-lg="2" vs-sm="2" vs-xs="2">
          <span v-if="application.showCount" class="red050 small-text">{{ application.count }}</span>
        </vs-col>
      </vs-row>
    </div>

    <div class="input-item">
      <button
        class="btn btn_sm btn_full"
        :disabled="application.verifyDim"
        @click="verifyPhoneNum"
      >
        인증 완료하기
      </button>
    </div>

    <div class="content">
      <small v-if="creditInquiryAgreement">
        인증 완료하기 버튼 클릭 시 <a href="#" style="text-decoration:underline" @click.prevent="openPage('https://socar-docs.zendesk.com/hc/ko/articles/360050811413')">신용 정보 조회 동의</a>가 완료됩니다.<br>
      </small>
      <small>
        계약을 위한 신용 조회는 신용점수에 영향을 미치지 않습니다.<br>
      </small>
    </div>

    <vs-row v-if="!creditInquiryAgreement" class="form-group submit mb-2">
      <vs-col vs-lg="6" vs-sm="6" vs-xs="12" class="bottom-button">
        <button
          type="submit"
          class="btn btn_lg btn_full btn_outline"
          @click.prevent="back"
        >
          이전
        </button>
      </vs-col>
      <vs-col vs-lg="6" vs-sm="6" vs-xs="12" class="bottom-button">
        <button
          type="submit"
          class="btn btn_lg btn_full"
          :disabled="!valid"
          @click.prevent="next"
        >
          다음
        </button>
      </vs-col>
    </vs-row>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import config from 'config';
import ApiClient, { API } from 'api/client';
import { DateTime, Duration } from 'luxon';
import { toNumber, trim, cloneDeep, isEmpty } from 'lodash';
import DialogUtil from 'utils/dialog';
import HeaderUtil from 'utils/header';
import OpenAppUtil from 'utils/openApp';
import * as amplitude from '@amplitude/analytics-browser';
import { isWebview } from 'utils/webview/isWebview';
import { closeWebView } from 'utils/webview/closeWebView';

export default {
  name: 'Agreement',
  mixins: [ApiClient],
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
    creditInquiryAgreement: {
      type: Boolean,
      default: false,
    },
    applicationId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      application: cloneDeep(this.data),
    };
  },
  computed: {
    phoneNum() {
      this.updatePhoneNum();
      return this.$store.state.user.phone;
    },
    valid() {
      return this.application.verifiedToken != null;
    },
  },
  created() {
    const { regionGroupId, carType, carTypeId, prepaidRate } = this.application;
    this.$gtag.event('view', {
      page_name: 'plan_contract_2_need_credit',
      event_label: regionGroupId,
      event_category: document.title,
      page_url: document.URL,
      component_state: prepaidRate === 0 ? '' : `prepayments_${prepaidRate * 100}%`,
      component_type: carType.thumbnailTags.map(({ id }) => id).join(','),
      component_category: carType.carClassTag.id || '',
      component_id: carTypeId,
      service_type: carType.benefitTags.map(({ id }) => id).join(','),
    });
  },
  methods: {
    ...mapActions('user', ['updatePhone']),
    async updatePhoneNum() {
      if (isEmpty(this.$store.state.user.phone)) {
        const cookieName = HeaderUtil.isWebView() ? 'auth_token' : 'plan_auth_token';
        const authToken = HeaderUtil.getCookie(cookieName);
        const SsoLoginApi = this.getApi(API.SSOLOGIN);
        const { phone } = await SsoLoginApi.checkSsoToken({ accessToken: authToken });
        this.updatePhone({ phone });
      }
    },
    openPage(url) {
      if (HeaderUtil.isAndroid()) {
        OpenAppUtil.openExternal(url);
      } else {
        window.open(url, '_blank');
      }
    },
    certifyPhoneNum() {
      const SmsAuthApi = this.getServerApi(API.SMS_AUTH);
      this.application.showCountMessage = false;
      this.application.verifyDim = false;
      this.application.sendDim = true;
      SmsAuthApi.send({
        phone: this.phoneNum,
      }).then((res) => {
        DialogUtil.alert('인증번호가 발송 되었습니다.');
        this.application.sendDim = false;
        clearInterval(this.timer);
        const { txnToken, expiresAt } = res.data;
        this.application.smsToken = txnToken;
        const currentTime = DateTime.utc().ts;
        const diffTime = toNumber(expiresAt) - currentTime;
        let duration = Duration.fromMillis(diffTime);
        const interval = 1000;
        this.timer = setInterval(() => {
          duration = Duration.fromMillis(duration.milliseconds - interval);
          let m = duration.shiftTo('minutes', 'seconds', 'milliseconds').minutes;
          let s = duration.shiftTo('minutes', 'seconds', 'milliseconds').seconds;
          if (m === 0 && s === 0) {
            this.application.showCountMessage = true;
            this.application.showCount = false;
            this.application.verifyDim = true;
            clearInterval(this.timer);
            return;
          }
          m = trim(m).length === 1 ? `0${m}` : m;
          s = trim(s).length === 1 ? `0${s}` : s;
          this.application.showCount = true;
          this.application.count = `${m}:${s}`;
        }, interval);
      }, () => {
        DialogUtil.alert('인증번호 발송에 실패하였습니다.');
        this.application.verifyDim = true;
        this.application.sendDim = false;
      });
    },
    verifyPhoneNum() {
      if (!(this.application.confirmNum)) {
        DialogUtil.alert('인증번호를 입력해주세요.');
        return;
      }
      const SmsAuthApi = this.getServerApi(API.SMS_AUTH);
      const CreditInquiryAgreementsApi = this.getApi(API.CREDIT_INQUIRY_AGREEMENT);
      SmsAuthApi.verify({
        authCode: this.application.confirmNum,
        smsToken: this.application.smsToken,
      }).then(async (res) => {
        const { verifiedToken } = res.data;
        this.application.verifiedToken = verifiedToken;
        this.application.showCountMessage = false;
        this.application.showCount = false;
        this.application.verifyDim = true;
        this.application.sendDim = true;
        clearInterval(this.timer);
        if (this.creditInquiryAgreement) {
          await CreditInquiryAgreementsApi.createCreditInquiryAgreement({ hashingId: this.applicationId })
            .then(() => {
              DialogUtil.alert('본인 확인 및 신용 정보 조회 동의가 완료되었습니다.\n이제 연장을 신청할 수 있습니다.');
              if (isWebview()) {
                closeWebView();
                return;
              }
              window.location.href = config.planHome.baseURL;
            })
            .catch(() => {
              if (isWebview()) {
                closeWebView();
                return;
              }
              window.location.href = config.planHome.baseURL;
            });
        } else {
          await CreditInquiryAgreementsApi.createCreditInquiryAgreement({})
            .then(({ id }) => {
              this.application.creditInquiryAgreementId = id;
              DialogUtil.alert('본인 확인 및 신용 정보 조회 동의가 완료되었습니다.');
              this.$emit('save', this.application);
            })
            .catch(() => {
              if (isWebview()) {
                closeWebView();
                return;
              }
              window.location.href = config.planHome.baseURL;
            });
        }
      }, (err) => {
        const { data } = err.response;
        DialogUtil.alert(data.code === 'TOKEN_EXPIRED' ? '유효기간이 경과하였습니다.\n인증번호를 다시 발송해주세요.' : '인증번호가 일치하지 않습니다.\n입력한 번호를 다시 확인해주세요.');
      });
    },
    next() {
      if (this.valid) {
        const { contractId } = this.application;
        const args = {
          page_name: 'plan_car_contract',
          event_category: document.title,
          page_url: document.URL,
          component_name: 'plan_check_credit_next_button',
          reservation_id: contractId,
        };
        this.$gtag.event('click', args);
        amplitude.logEvent('click', args);

        this.$emit('next', this.application);
      }
    },
    back() {
      this.$emit('back');
    },
  },
};
</script>

<style lang="scss" scoped>
.small-text {
  font-size: 12px !important;
}
.height-20 {
  height: 20px;
}
.mt-15 {
  margin-top: 15px !important;
}
.padding {
  @media only screen and (max-width: 768px)  {
    padding-right: 10px !important;
    padding-left: 10px !important;
  }
  @media only screen and (max-width: 340px)  {
    padding-right: 5px !important;
    padding-left: 5px !important;
  }
}
</style>
